import React, { useEffect } from 'react'
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, Image, Font } from '@react-pdf/renderer'
import moment, { Moment } from 'moment'
import * as Datetime from 'react-datetime'
import Select from 'react-select'
import { ITripFilters, IActiveTripResponseV2, IProviderProductObject, ITripStatusesObj } from 'src/types'
import { metersToMiles } from 'src/utils'
import UIMessages from 'src/utils/ui-messages'

interface IAfterHoursReportPDFProps {
  match: any
  orgId: string
  trips: IActiveTripResponseV2[]
  serviceProducts?: IProviderProductObject[]
  getManyTrips: (params: ITripFilters) => IActiveTripResponseV2
  setTripsEmpty: () => void
  orgName: string
}

interface ISelectOption {
  label: string
  value: any
}

Font.register({
  family: 'OpenSans',
  fonts: [
    {
      src: `/fonts/opensans/OpenSans-Regular.ttf`
    },
    {
      src: `/fonts/opensans/OpenSans-Bold.ttf`,
      fontWeight: 'bold'
    }
  ]
})

const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    backgroundColor: '#E4E4E4'
  },

  headerTop: {
    flexDirection: 'row',
    marginBottom: 10
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: '100'
  },
  headerLeftInfo: {
    width: '50%',
    paddingLeft: 10
  },
  headerRightInfo: {
    width: '50%',
    paddingRight: 10
  },
  headerData: {
    fontSize: '12',
    borderBottom: '1px solid black',
    paddingBottom: 2
  },
  headerCaption: {
    fontSize: 8
  },
  section: {
    marginLeft: 10,
    marginTop: 20,
    padding: 10,
    flexGrow: 1,
    fontSize: 14
    // fontWeight:
  },

  font: {
    fontSize: 10,
    marginRight: 20,
    marginTop: 20
  },

  /* Table */
  table: {
    fontSize: 10,
    width: 1000,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignContent: 'stretch',
    flexWrap: 'nowrap',
    alignItems: 'stretch'
  },
  row: {
    // display: 'flex',
    flexDirection: 'row'
    // justifyContent: 'space-around',
    // alignContent: 'stretch',
    // flexWrap: 'nowrap',
    // alignItems: 'stretch',
    // flexGrow: 0,
    // flexShrink: 0,
    // flexBasis: 35
  },
  cell: {
    /* borderColor: 'black',
    borderStyle: 'solid',
    borderWidth: 1, */
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    alignSelf: 'stretch',
    fontSize: 10
    /* marginLeft: 20,
    marginRight:20 */
  },
  header: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black'
  },
  headerText: {
    fontSize: 11,
    color: 'black',
    verticalAlign: 'middle'
  },
  tableText: {
    margin: 10,
    marginLeft: 10,
    fontSize: 10,
    color: 'black'
  },
  body: {
    paddingTop: 30,
    paddingBottom: 10,
    paddingHorizontal: 20
  },
  image: {
    height: 45,
    width: 170
  },
  imageSignature: {
    marginTop: -16,
    height: 32,
    width: 40,
    alignSelf: 'center'
  },
  SectionHeader: {
    borderLeft: '2px solid black',
    borderBottom: '2px solid black',
    borderRight: '2px solid black',
    borderTop: '2px solid black'
  },
  bolderFontStyle: {
    fontSize: 10,
    fontFamily: 'OpenSans',
    fontWeight: 'bold'
  },
  SectionHeaderBottom: {
    borderBottom: '2px solid black'
  },
  headerTwoLines: {
    textAlign: 'center',
    borderLeft: '1px solid black',
    minHeight: '2.5em',
    padding: 5
  },
  dataLine: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    minHeight: '1.2em',
    fontSize: 10,
    paddingVertical: 5,
    paddingLeft: 5,
    paddingRight: 5
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20
  }
  /* Table */
})

const env = process.env.REACT_APP_ENV

const AfterHoursReportPDF = (props: IAfterHoursReportPDFProps) => {
  const [fromDateFilter, setFromDate] = React.useState(
    moment()
      .startOf('day')
      .subtract(7, 'days')
      .toISOString()
  )
  const [toDateFilter, setToDate] = React.useState(
    moment()
      .endOf('day')
      .toISOString()
  )
  const [filterMessage, setFilterMessage] = React.useState('')
  const [fromDateDisplay, setFromDateDisplay] = React.useState(
    moment()
      .startOf('day')
      .subtract(7, 'days')
      .format('MM/DD/YYYY hh:mmA')
  )
  const [toDateDisplay, setToDateDisplay] = React.useState(
    moment()
      .endOf('day')
      .format('MM/DD/YYYY hh:mmA')
  )
  const [productIds, setProductIds] = React.useState([''])
  const [options, setOptions] = React.useState([{ label: '', value: '' }])
  const serviceId = props.match.params.id
  const filename = `AfterHoursReport-${moment().format('YYYY-MM-DD HH:MM:SS')}`
  const [isSubmitted, setIsSubmited] = React.useState(false)
  const [serviceProductsLoaded, setServiceProductsLoaded] = React.useState(false)
  const [trips, setTrips] = React.useState<IActiveTripResponseV2[]>([] as IActiveTripResponseV2[])
  const [shouldLoadData, setShouldLoadData] = React.useState(false)
  useEffect(() => {
    props.setTripsEmpty()
    return () => {
      props.setTripsEmpty()
      setShouldLoadData(false)
    }
  }, [])
  // will try to depend on self state not from the redux
  useEffect(() => {
    if (shouldLoadData && props.trips && Object.values(props.trips).length > 0) {
      setTrips(Object.values(props.trips))
    }
  }, [props.trips])
  const setFromDateFilter = (e: Moment) => {
    if (moment(e).isValid()) {
      const fromDate = e.startOf('day')
      const fromDateISO = fromDate.toISOString()
      const toDate = moment(toDateFilter).endOf('day')

      if (fromDate > toDate) {
        setFilterMessage(`Please choose a earlier date than ${toDateDisplay}`)
        return
      } else {
        setFilterMessage('')
      }
      setFromDate(fromDateISO)
      setToDate(toDate.toISOString())
      setFromDateDisplay(fromDate.format('MM/DD/YYYY hh:mmA'))
      setToDateDisplay(toDate.format('MM/DD/YYYY hh:mmA'))
      setTrips([])
    }
  }
  const setToDateFilter = (e: Moment) => {
    if (moment(e).isValid()) {
      const toDate = e.endOf('day')
      const toDateISO = toDate.toISOString()
      const fromDate = moment(fromDateFilter).startOf('day')

      if (fromDate > toDate) {
        setFilterMessage(`Please choose a later date ${fromDateDisplay}`)
        return
      } else {
        setFilterMessage('')
      }
      setFromDate(fromDate.toISOString())
      setToDate(toDateISO)
      setFromDateDisplay(fromDate.format('MM/DD/YYYY hh:mmA'))
      setToDateDisplay(toDate.format('MM/DD/YYYY hh:mmA'))
      setTrips([])
    }
  }

  const selectProductIds = (selectedIds: ISelectOption[]) => {
    let productIds = []
    if (selectedIds) {
      productIds = selectedIds.map((op: any) => op.value)
    }
    setProductIds(productIds)
    setTrips([])
  }

  const formatDateTime = (date: string | undefined, format: 'MM-DD-YYYY' | 'hh:mmA') => {
    if (date) {
      return moment(date).format(format)
    }
    return 'no data'
  }

  const getBucketName = () => {
    return process.env.REACT_APP_ENV === 'production'
      ? process.env.REACT_APP_S3_BUCKET_PROD
      : process.env.REACT_APP_S3_BUCKET_DEV
  }

  // below code is for testing purposes, undeleted for fast testing connected in prod
  // const getBucketName = () => {
  //   return process.env.REACT_APP_S3_BUCKET_PROD
  // }

  const getTime = (date: string | undefined, dateFormat: string = 'hh:mm A'): string | null => {
    const initDate = moment(date)
    return initDate.isValid() ? initDate.format(dateFormat) : ' '
  }
  const getStatusDateTime = (
    statuses: ITripStatusesObj[],
    shouldStatusContainOne: number[],
    dateFormat: string = 'hh:mm A'
  ): string | null => {
    const stats = statuses.find(status => {
      return shouldStatusContainOne.find(n => status.statusId === n)
    })
    if (stats) {
      return getTime(stats.createdAt, dateFormat)
    }
    return null
  }

  const getRanBy = (statuses: ITripStatusesObj[]): string | null => {
    const stat = statuses.find(status => {
      return [1, 21, 23].find(n => status.statusId === n)
    })
    if (stat && stat.user) {
      return `${stat.user.firstName} ${stat.user.lastName}`
    }
    return ''
  }

  const isPFPservice = () => {
    switch (env) {
      case 'production':
        if (serviceId === 'e5980aa6-dde9-46bd-b495-7ed6d96444b8') {
          return true
        }
        return false
      default:
        if (serviceId === '9eee3c5b-61bb-457b-8f1e-7199d08162bf') {
          return true
        }
        return false
    }
  }
  // const isPFPservice = () => 'e5980aa6-dde9-46bd-b495-7ed6d96444b8'

  const AfterHoursReportPDF = (props: { trips: IActiveTripResponseV2[]; orgName: string }) => {
    if (props.trips) {
      return (
        <Document>
          {Object.values(props.trips).map(trip => {
            // const pickup = trip.status.find(item => item.statusId === 5)
            // const dropoff = trip.status.find(item => item.statusId === 7)
            const repSignature =
              trip.representativeSignature !== '' && trip.representativeSignature !== null
                ? `https://${getBucketName()}.s3.amazonaws.com/uploads/signatures/rep/${trip.representativeSignature}`
                : null
            // const driverSignature =
            //   trip.driverSignature !== '' && trip.driverSignature !== null
            //     ? `https://${getBucketName()}.s3.amazonaws.com/uploads/signatures/driver/${trip.driverSignature}`
            //     : null
            const distance = trip.distance ? `${metersToMiles(trip.distance)}` : 'Unavailable'
            const estimatedDistance = trip.estimatedDistance
              ? `${metersToMiles(trip.estimatedDistance)}`
              : 'Unavailable'

            return (
              <Page size='A4' style={styles.body} key={trip.id}>
                {/* Headers */}
                <View style={styles.row}>
                  <View style={{ width: '20%', flexDirection: 'column' }}>
                    {/* <Text style={{ fontSize: '', marginLeft: 10 }}>Image Here</Text> */}
                    <Image
                      style={styles.image}
                      // src={`https://goin-dev-files.s3.amazonaws.com/Cabulance.PNG`}
                      src={`https://goin-dev-files.s3.amazonaws.com/medstar.JPG`}
                      cache={true}
                    />
                  </View>
                  <View style={{ width: '50%', flexDirection: 'column' }}>
                    <Text>{'\n'}</Text>
                    <Text style={{ fontSize: '16', marginLeft: 40, fontWeight: 700, marginTop: 10 }} />
                  </View>
                  <View style={{ width: '30%', flexDirection: 'column' }}>
                    <Text>{'\n'}</Text>
                                         <Text style={{ fontSize: '10', marginRight: 10, marginLeft: 10 }}>
                       Confirmation #:{' '}
                       <span style={{ fontSize: '11', fontFamily: 'OpenSans', fontWeight: 'bold' }}>
                         {' '}
                         {trip.confirmationNumber ? trip.confirmationNumber : ' '}
                       </span>
                    </Text>
                    <Text style={{ fontSize: '10', marginRight: 10, marginLeft: 10 }}>
                      Ran By : {getRanBy(trip.status) ? getRanBy(trip.status) : ' '}
                    </Text>
                  </View>
                </View>
                {/* Headers */}

                {/* Section 1 */}
                <View style={[styles.row, styles.SectionHeader]}>
                  {/*   <View style={{ width: '20%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Medstar #: {'000-00000'}</Text>
                  </View> */}

                  <View style={{ width: '50%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '12', marginLeft: 1, fontWeight: 700 }}>
                      Section 1 - Client Information
                    </Text>
                  </View>

                  <View style={{ width: '30%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '10', marginRight: 10, marginLeft: 10 }}>{''}</Text>
                  </View>
                </View>

                {/* Section 1 */}

                {/* Body 1 */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Trip Date:</Text>
                    <Text style={{ fontSize: '10' }}>Client Name:</Text>
                    <Text style={{ fontSize: '10' }}>Service Level:</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>{getStatusDateTime(trip.status, [5], 'MM/DD/YYYY')} </Text>
                    <Text style={{ fontSize: '10' }}>{trip.rider.firstName + ' ' + trip.rider.lastName}</Text>
                    <Text style={{ fontSize: '10' }}>{trip.product.name} </Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Provider One ID:</Text>
                    <Text style={{ fontSize: '10' }}>Client DOB: </Text>
                    <Text style={{ fontSize: '10' }}>Client Mobility:</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column' }}>
                    <Text style={[styles.bolderFontStyle, { fontSize: '10' }]}>
                       {trip.providerOneNumber ? trip.providerOneNumber : ' '}
                    </Text>
                    <Text style={{ fontSize: '10' }}>
                      {trip.rider && trip.rider.birthDate ? getTime(trip.rider.birthDate, 'YYYY-MM-DD') : ' '}
                    </Text>
                    <Text style={{ fontSize: '10' }}>{trip.product.name} </Text>
                  </View>
                </View>

                {/* Body 1 */}

                {/* Section 2 */}
                <View style={[styles.row, styles.SectionHeader]}>
                  {/*   <View style={{ width: '20%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>{''}</Text>
                  </View> */}

                  <View style={{ width: '50%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '12', marginLeft: 1, fontWeight: 700 }}>Section 2 - Trip Information</Text>
                  </View>

                  <View style={{ width: '30%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '10', marginRight: 10, marginLeft: 10 }}>{''}</Text>
                  </View>
                </View>
                {/* Section 2 */}

                {/* Body 2 */}

                <View style={[styles.row]}>
                  <View style={{ width: '1000%', marginLeft: 10, marginTop: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Address where client was picked up: {trip.pickupLocation.fullAddress}
                    </Text>
                  </View>
                </View>
                {/* New Row */}

                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Phone : {trip.rider.phoneNumber}</Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Pickup Time : {getStatusDateTime(trip.status, [5])}</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>P/U Mileage: {trip.startMileage ? trip.startMileage : ' '}</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}># of total riders: {trip.riderCount ? trip.riderCount : ''}</Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '100%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      ER Visit or Hosp. Discharge Room Number and Trip Notes from Medstar Dispatch:{' '}
                    </Text>
                    <Text style={{ fontSize: '10', marginTop: 10 }}>{trip.notes ? trip.notes : ' '}</Text>
                    {/* New Row */}
                    <View style={[styles.row, { marginTop: 10, marginBottom: 10 }, styles.SectionHeaderBottom]}>
                      <Text style={{ fontSize: '10', marginTop: 10 }}>{''}</Text>
                    </View>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row]}>
                  <View style={{ width: '100%', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Address where client was dropped off: {trip.dropoffLocation.fullAddress}
                    </Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Phone: {trip.rider.phoneNumber}</Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Drop Off Time: {getStatusDateTime(trip.status, [7, 8, 9])}</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Drop Off Mileage: {trip.endMileage ? trip.endMileage : ' '}</Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    {!trip.estimatedDistance && trip.estimatedDistance > 0 ? (
                      <Text style={{ fontSize: '10' }}>Est. Miles Google Maps: {estimatedDistance}</Text>
                    ) : (
                      <Text style={{ fontSize: '10' }}>Actual Distance in Miles: {distance}</Text>
                    )}
                  </View>
                </View>

                {/* Body 2 */}

                {/* Section 2 */}
                <View style={[styles.row, styles.SectionHeader]}>
                  {/*   <View style={{ width: '20%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>{''}</Text>
                  </View> */}

                  <View style={{ width: '50%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '12', marginLeft: 1, fontWeight: 700 }}>
                      Section 3 - Verification and Certification
                    </Text>
                  </View>

                  <View style={{ width: '30%', flexDirection: 'column' }}>
                    <Text style={{ fontSize: '10', marginRight: 10, marginLeft: 10 }}>{''}</Text>
                  </View>
                </View>
                {/* Section 2 */}

                {/* Body 3 */}

                {!isPFPservice() && (
                  <View style={[styles.row]}>
                    <View style={{ width: '100%', flexDirection: 'column', marginLeft: 10, marginTop: 10 }}>
                      <Text style={{ fontSize: '10' }}>
                        Broker will post verify that the client received a Medicaid eligible service prior to
                        authorizing the After-Hours Trip
                      </Text>
                    </View>
                  </View>
                )}

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10, alignItems: 'flex-start' }]}>
                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Transportation Provider : {props.orgName ? props.orgName : ' '}
                    </Text>
                  </View>

                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10, alignItems: 'flex-start' }}>
                    <Text style={{ fontSize: '10', alignItems: 'flex-start' }}>
                      Driver Name:
                      <span style={{ fontFamily: 'OpenSans', fontWeight: 'bold' }}>
                         {' '}
                         {trip.operator ? trip.operator.firstName + ' ' + trip.operator.lastName : ' '}
                       </span>
                     </Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '100%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Trip Requested By: {trip.tripRequester}</Text>
                  </View>
                  <View style={{ width: '100%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Trip Notes and Phone Number: {trip.notes + ' ' + trip.rider.phoneNumber}
                    </Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Date Trip Requested: {moment(trip.createdAt ? trip.createdAt : '').format('MM-DD-YYYY')}
                    </Text>
                  </View>
                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      Time Requested: {moment(trip.createdAt ? trip.createdAt : '').format('hh:mm A')}
                    </Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '100%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                      I hereby certify, to the best of my knowledge that the information listed herein for
                      Medicaid-covered services rendered is accurate and the services have been provided as authorized
                      and without discrimination on the grounds of race, creed, color, national origin, or sex.
                    </Text>
                  </View>
                </View>
                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '75%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>
                        Printed Med Staff Name and Title:{' '}
                       <span style={{ fontFamily: 'OpenSans', fontWeight: 'bold' }}>
                         {trip.representativeName ? trip.representativeName : ' '}{' '}
                         {trip.representativeTitle ? trip.representativeTitle : ' '}
                       </span>
                    </Text>
                  </View>

                  <View style={{ width: '25%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>Date: {getStatusDateTime(trip.status, [5], 'MM/DD/YYYY')}</Text>
                  </View>
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <div
                      style={{
                        fontSize: '10',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignContent: 'flex-end',
                        flexDirection: 'row',
                        height: 36
                      }}
                    >
                    <Text style={{ alignSelf: 'center', marginRight: 4 }}>Med. Staff Sig:</Text>
                      {repSignature && repSignature !== '' && repSignature !== null ? (
                        <Image style={styles.imageSignature} src={repSignature} cache={true} />
                      ) : (
                        <Text style={{ backgroundColor: '#FFFF00' }} />
                      )}
                    </div>
                  </View>
                  {!isPFPservice() && (
                    <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                      <div style={{ height: 36, alignContent: 'flex-end', flexDirection: 'row' }}>
                        <Text style={{ fontSize: '10', alignSelf: 'center' }}>Pickup Sig. </Text>
                      </div>
                    </View>
                  )}
                  {!isPFPservice() && (
                    <View style={{ width: '50%', flexDirection: 'column', height: 32, marginLeft: 10 }}>
                      <div style={{ height: 36, alignContent: 'flex-end', flexDirection: 'row' }}>
                        <Text style={{ fontSize: '10', alignSelf: 'center' }}>Dropoff Sig. </Text>
                      </div>
                    </View>
                  )}
                </View>

                {/* New Row */}
                <View style={[styles.row, { marginTop: 10, marginBottom: 10 }]}>
                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>{''}</Text>
                  </View>

                  <View style={{ width: '50%', flexDirection: 'column', marginLeft: 10 }}>
                    <Text style={{ fontSize: '10' }}>{''}</Text>
                  </View>
                </View>
                {isPFPservice() && (
                  <div>
                    <View style={[styles.row, { marginTop: 10, marginBottom: 10 }, styles.SectionHeaderBottom]}>
                      <Text style={{ fontSize: '10', marginTop: 10 }}>{''}</Text>
                    </View>

                    <View style={[styles.row, { marginTop: 10, marginBottom: 2 }]}>
                      <Text style={{ fontSize: '10', marginTop: 10 }}>
                        Please fax this form the next business day after the service or payment could be denied. If
                        information is not completed or signed, the after hours fee will not be authorized.
                      </Text>
                    </View>

                    <View style={[styles.row, { marginBottom: 10 }]}>
                      <Text style={{ fontSize: '12', marginTop: 10, fontStyle: 'bold' }}>
                        People For People Transportation Broker Fax Number: (509) 574-5085
                      </Text>
                    </View>
                  </div>
                )}

                {/* Body 3 */}
              </Page>
            )
          })}
        </Document>
      )
    }
    return <div />
  }
  React.useEffect(() => {
    const params: ITripFilters = {
      serviceId,
      orgId: props.orgId,
      take: 10000,
      timeField: 'tripCreated',
      timeStart: fromDateFilter,
      timeEnd: toDateFilter,
      statusGroup: 'completed',
      productIds //add Product id
    }
    if (isSubmitted) {
      props.getManyTrips(params)
      setIsSubmited(false)
    }
  }, [isSubmitted])

  React.useEffect(() => {
    if (!serviceProductsLoaded) {
      if (props.serviceProducts && props.serviceProducts.length > 0) {
        const productIds = props.serviceProducts.map(product => {
          return { label: product.name, value: product.id }
        })
        setOptions(productIds)
        setServiceProductsLoaded(true)
      }
    }
  }, [props.serviceProducts])

  return (
    <div>
      <label className='ml1 h5 font-red'>{filterMessage}</label>
      <div className='flex mb2 g-align-items-end'>
        <div className='ml1 col-2'>
          <label className='h5'>From</label>
          <Datetime
            className='mt1 p1 rounded border-light-grey4'
            value={fromDateDisplay}
            onChange={setFromDateFilter}
          />
        </div>
        <div className='ml1 col-2'>
          <label className='h5'>To</label>
          <Datetime className='mt1 p1 rounded border-light-grey4' value={toDateDisplay} onChange={setToDateFilter} />
        </div>
        <div className='ml1 col-2'>
          <label className='h5'>Select Product</label>
          <Select
            classNamePrefix='select-control'
            options={options}
            value={options.filter((op: any) => productIds.includes(op.value))}
            onChange={selectProductIds}
            isMulti={true}
          />
        </div>
        <div className='ml1'>
          <button
            className='btn bg-green rounded font-white weight-200'
            onClick={() => {
              if (props.serviceProducts) {
                const productIds = props.serviceProducts.map(product => {
                  return { label: product.name, value: product.id }
                })
                setOptions(productIds)
                setIsSubmited(true)
                setShouldLoadData(true)
              }
            }}
          >
            {UIMessages.service.generate}
          </button>
        </div>
      </div>

      <PDFDownloadLink document={<AfterHoursReportPDF orgName={props.orgName} trips={trips} />} fileName={filename}>
        {({ blob, url, loading, error }) =>
          loading ? 'Download now!(Loading document...)' : trips.length > 0 ? 'Download now!' : ''
        }
      </PDFDownloadLink>
    </div>
  )
}

export default AfterHoursReportPDF



// WEBPACK FOOTER //
// ./src/components/services/view/pdfs/AfterHoursReportPDF.tsx